import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { useTicketChat } from 'store/useTicketChat'
import FormattedMessage from 'utils/FormattedMessage'
import { useTranslation } from 'utils/useTranslation'

interface TabChatProps {
  placeId: string
  appointmentId: string
}

const LINE_HEIGHT = 24 // px — matches text-sm line-height
const MAX_ROWS = 4

export const TabChat = ({ placeId, appointmentId }: TabChatProps) => {
  const intl = useTranslation()
  const { messages, isLoading, subscribe, send, markRead } = useTicketChat()
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const unsubscribe = subscribe(placeId, appointmentId)
    return unsubscribe
  }, [placeId, appointmentId, subscribe])

  useEffect(() => {
    markRead()
  }, [placeId, appointmentId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Auto-resize textarea up to MAX_ROWS, then scroll
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, LINE_HEIGHT * MAX_ROWS)}px`
  }, [text])

  const handleSend = async () => {
    const trimmed = text.trim()
    if (!trimmed || sending) return
    setSending(true)
    try {
      await send(trimmed)
      setText('')
    } catch (e) {
      console.error('Failed to send message', e)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className='flex flex-col px-4 py-3 min-h-[200px]'>
      {!isLoading && messages.length === 0 && (
        <p className='text-sm text-gray-400 text-center mt-4'>
          <FormattedMessage id='ticket.chat.empty' />
        </p>
      )}
      <div className='flex flex-col gap-2 mb-3'>
        {messages.map(msg => {
          const time = msg.createdAt?.toMillis
            ? DateTime.fromMillis(msg.createdAt.toMillis()).toLocaleString(DateTime.DATETIME_SHORT)
            : ''
          return (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.sender === 'visitor' ? 'items-end' : 'items-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                  msg.sender === 'visitor'
                    ? 'bg-custom-accent text-white rounded-br-none'
                    : 'bg-gray-100 text-gray-800 rounded-bl-none'
                }`}
              >
                {msg.text}
              </div>
              {time && <span className='text-xs text-gray-400 mt-0.5 px-1'>{time}</span>}
            </div>
          )
        })}
        <div ref={messagesEndRef} />
      </div>
      <div className='flex mt-auto pt-2 border-t border-gray-200'>
        <div className='flex flex-1 items-center rounded-2xl border border-gray-300'>
          <textarea
            ref={textareaRef}
            rows={1}
            className='flex-1 resize-none bg-transparent border-0 text-sm overflow-y-auto px-3 py-2.5'
            style={{ lineHeight: `${LINE_HEIGHT}px`, maxHeight: `${LINE_HEIGHT * MAX_ROWS}px`, outline: 'none', boxShadow: 'none' }}
            placeholder={intl({ id: 'ticket.chat.inputPlaceholder' })}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void handleSend()
              }
            }}
            disabled={sending}
          />
          <button
            className='flex-shrink-0 w-9 h-9 m-1 self-end rounded-full bg-custom-accent flex items-center justify-center disabled:opacity-50'
            onClick={handleSend}
            disabled={!text.trim() || sending}
            aria-label='Send'
          >
            {sending ? (
              <svg
                className='w-[18px] h-[18px] animate-spin text-white'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z' />
              </svg>
            ) : (
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 24 24'
                fill='white'
                className='w-[18px] h-[18px]'
              >
                <path d='M2.01 21L23 12 2.01 3 2 10l15 2-15 2z' />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
